


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { Typography } from '@mui/material';
import {
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    GroupedField,
    GroupedFields,
    FormBreadcrumbs,
    ICrfConditionParameters,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import { THREE_DIGIT_POSITIVE_INTEGER } from '../../../utils/formatting/NumericFormats';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const speaksEnglish = ({ formState }: ICrfConditionParameters<Dtos.Proms>) => formState?.value?.speaksEnglish === Dtos.LookupYesNoType.Yes;
const carerResponsibilities = ({ formState }: ICrfConditionParameters<Dtos.Proms>) => formState?.value?.carerResponsibilities === Dtos.LookupYesNoType.Yes;
const parityMoreThanZero = ({ formState }: ICrfConditionParameters<Dtos.Proms>) => (formState?.value?.parity ?? 0) > 0;
/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

interface IPROMsFormProps { }

const PROMsForm: React.FunctionComponent<IPROMsFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedField
                    propertyPath="speaksEnglish"
                    component={ToggleButtonGroup}
                />
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={speaksEnglish}
                    subscription={{ value: true }}
                >
                    <GroupedField
                        propertyPath="highestEducationLevel"
                        component={Select}
                    />
                    <GroupedField
                        propertyPath="relationshipStatus"
                        component={Select}
                    />

                    <GroupedFields>
                        <GroupedField
                            propertyPath="carerResponsibilities"
                            component={ToggleButtonGroup}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={carerResponsibilities}
                            subscription={{ value: true }}
                        >
                            <GroupedField
                                propertyPath="dependentChildren"
                                component={Checkbox}
                                label=""
                            />
                            <GroupedField
                                propertyPath="dependentAdults"
                                component={Checkbox}
                                label=""
                            />
                        </CrfCondition>
                    </GroupedFields>

                    <GroupedField
                        propertyPath="parity"
                        component={Numeric}
                        numberFormat={THREE_DIGIT_POSITIVE_INTEGER}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={parityMoreThanZero}
                        subscription={{ value: true }}
                    >
                        <GroupedFields>
                            <Typography sx={{ px: 2, py: 1}}>Age of youngest child?</Typography>
                            <GroupedField
                                propertyPath="ageOfYoungestChildLength"
                                component={Select}
                            />
                            <GroupedField
                                propertyPath="ageOfYoungestChild"
                                component={Numeric}
                                numberFormat={THREE_DIGIT_POSITIVE_INTEGER}
                            />
                        </GroupedFields>
                    </CrfCondition>
                    <GroupedField
                        propertyPath="treatedForAnxietyOrDepression"
                        component={ToggleButtonGroup}
                    />
                </CrfCondition>
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PROMsForm;
